/**
 * "Done so far" progress rail for the onboarding flow.
 *
 * Single-section card showing 5 milestones that light as completion
 * happens. "Account created" is always lit (signup is prereq for
 * being here). The other four light from real completion flags
 * (snippet_verified_at, google_ads_customer_id + facebook_pixel_id,
 * waba_id) — NOT from skip flags. Skipped milestones look identical
 * to future (unlit empty circle); the dashboard DashboardBanners
 * carries the "come back and finish this" reminder.
 *
 * State is passed in as a prop so the rail stays decoupled from
 * useAuth(). Pages compute state from me.* fields and pass down.
 *
 * Variant controls placement styling:
 *   - "desktop": sticky inside the right column of the 2-column shell
 *   - "mobile":  full-card width above the H1 on small viewports
 *
 * See STATUS/PLAN_ONBOARDING_REDESIGN_PORT.md.
 */

import { cn } from '@/lib/utils';

export interface OnboardingRailState {
  /** Always true once the user reaches onboarding (signup completed). */
  accountCreated: boolean;
  /** clients.name (business name) is non-null — Step 1 completed. */
  profileDone: boolean;
  /** clients.snippet_verified_at is non-null — Step 2 actually installed (NOT skipped). */
  snippetDone: boolean;
  /** Both Google + Meta ad platforms connected — Step 3 actually connected (NOT skipped). */
  adsDone: boolean;
  /** clients.waba_id (or waba_paired_at) is non-null — Step 4 WhatsApp paired. */
  whatsappDone: boolean;
  /** When true, all 5 milestones light (success page). */
  isSuccess?: boolean;
}

interface Props {
  state: OnboardingRailState;
  variant: 'desktop' | 'mobile';
}

const CheckIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="h-3 w-3 text-green-700">
    <path
      fillRule="evenodd"
      d="M16.704 5.293a1 1 0 0 1 .003 1.414l-7.5 7.55a1 1 0 0 1-1.42.003L3.29 9.797a1 1 0 1 1 1.42-1.41l3.79 3.82 6.79-6.835a1 1 0 0 1 1.414-.079Z"
      clipRule="evenodd"
    />
  </svg>
);

const MILESTONES: ReadonlyArray<{
  id: keyof OnboardingRailState | 'accountCreated';
  text: string;
}> = [
  { id: 'accountCreated', text: 'Account created' },
  { id: 'profileDone', text: 'Profile complete' },
  { id: 'snippetDone', text: 'Snippet installed' },
  { id: 'adsDone', text: 'Ad platforms connected' },
  { id: 'whatsappDone', text: 'WhatsApp paired' },
];

export function OnboardingRail({ state, variant }: Props) {
  const isDesktop = variant === 'desktop';
  const isSuccess = !!state.isSuccess;

  return (
    <aside
      className={cn(
        'rounded-3xl bg-card shadow-card',
        isDesktop && 'sticky top-8 p-6',
        !isDesktop && 'p-5',
      )}
    >
      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
        Done so far
      </p>
      <ul className="mt-3 space-y-3">
        {MILESTONES.map((m) => {
          const isLit = isSuccess || !!state[m.id as keyof OnboardingRailState];
          return (
            <li key={m.id} className="flex items-start gap-2.5">
              <span
                className={cn(
                  'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition-colors',
                  isLit ? 'bg-green-100' : 'bg-dark/5',
                )}
              >
                {isLit ? (
                  <CheckIcon />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
                )}
              </span>
              <span
                className={cn(
                  'text-sm leading-snug',
                  isLit ? 'text-dark font-medium' : 'text-muted-foreground',
                )}
              >
                {m.text}
              </span>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
